import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2 } from 'lucide-react';
import { useSocIncident } from '@/api/soc';
import { PageHeader } from '@/components/PageHeader';
import { Field, Panel } from '@/components/insights/RecordKit';
import { HealthChip, INDICATOR_LABEL, IncidentStatusChip, RmmMatchCell, SeverityChip, TicketLinks } from '@/components/soc/SocKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { ROUTES, companyPath } from '@/lib/routes';

/**
 * One Huntress incident (`/soc/incidents/:id`): the analyst's report (plain text from Huntress), the
 * remediations Huntress proposes and their status, the affected agent (→ Device Orbit) and the
 * Autotask ticket Huntress raised. Read-only: approve remediations / resolve in Huntress.
 */
export default function SocIncidentPage() {
  const id = Number(useParams<{ id: string }>().id);
  const { data, isLoading, error, isFetching } = useSocIncident(Number.isFinite(id) ? id : undefined);
  const i = data?.incident;

  return (
    <div className="mx-auto w-full max-w-[1300px] px-4 py-4 md:px-6">
      <Link to={ROUTES.soc.incidents} className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="h-3 w-3" /> Incidents</Link>
      {isLoading ? <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading the incident from Huntress…</div>
        : error || !i ? <div className="mt-4 text-sm text-destructive">{error instanceof Error ? error.message : 'Incident not found'}</div>
        : (
          <>
            <PageHeader title={i.summary ?? i.subject} isFetching={isFetching}
              subtitle={<>{i.companyId != null ? <Link to={companyPath(i.companyId)} className="hover:underline">{i.companyName}</Link> : i.orgName} · sent {fmtRelative(i.sentAt)}{i.closedAt && <> · closed {fmtRelative(i.closedAt)}</>}</>}
              actions={<a href={i.portalUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-muted">Open in Huntress <ExternalLink className="h-3 w-3" /></a>} />
            <div className="mt-2 flex flex-wrap items-center gap-2"><SeverityChip severity={i.severity} /><IncidentStatusChip status={i.status} />
              {i.indicatorTypes.map((t) => <span key={t} className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">{INDICATOR_LABEL[t] ?? t}</span>)}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
              <div className="space-y-4">
                <Panel title="Huntress report">
                  {data.error ? <div className="px-4 py-4 text-xs text-destructive">Couldn't load the report: {data.error}</div>
                    : data.body ? <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap break-words px-4 py-3 font-sans text-[13px] leading-relaxed text-foreground">{data.body}</pre>
                    : <div className="px-4 py-4 text-xs text-muted-foreground">No report text.</div>}
                </Panel>
                <Panel title={`Remediations${data.remediations.length ? ` · ${data.remediations.length}` : ''}`}>
                  {data.remediations.length === 0 ? <div className="px-4 py-4 text-xs text-muted-foreground">Huntress proposed no remediations for this incident.</div> : (
                    <ul className="divide-y divide-divider">
                      {data.remediations.map((r) => (
                        <li key={r.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                          <span>{r.action ?? r.type ?? `Remediation ${r.id}`}</span>
                          <span className="text-xs text-muted-foreground">{r.status ?? '—'}{r.completedAt && ` · ${fmtRelative(r.completedAt)}`}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="border-t border-divider px-4 py-2 text-[11px] text-muted-foreground">Approve or reject remediations in Huntress: Cosmos is read-only.</p>
                </Panel>
              </div>
              <div className="space-y-4">
                <Panel title="Autotask">
                  <div className="py-1"><Field label="Ticket"><TicketLinks ids={i.ticketIds} /></Field></div>
                </Panel>
                <Panel title="Affected host">
                  {!data.agent ? <div className="px-4 py-4 text-xs text-muted-foreground">{i.platform === 'microsoft_365' ? 'A Microsoft 365 identity incident (ITDR): no endpoint.' : 'The agent is no longer in Huntress.'}</div> : (
                    <div className="py-1">
                      <Field label="Host">{data.agent.hostname}</Field>
                      <Field label="Health"><HealthChip a={data.agent} /></Field>
                      <Field label="Datto RMM"><RmmMatchCell a={data.agent} /></Field>
                      <Field label="OS">{data.agent.os}</Field>
                      <Field label="Last callback">{fmtRelative(data.agent.lastCallback)}</Field>
                      <Field label="Isolation">{data.agent.isolated ? <span className="font-medium text-destructive">Isolated by Huntress</span> : "Not isolated"}</Field>
                    </div>
                  )}
                </Panel>
              </div>
            </div>
          </>
        )}
    </div>
  );
}
